import { useState } from "react";
import { Button } from "@/components/ui/button.jsx";
import { Copy, Check } from "lucide-react";
import { copyTextToClipboard } from "@/lib/clipboard-utils.js";
import { steelChromeButton } from "@/lib/status-styles.js";
import { cn } from "@/lib/utils.js";

/**
 * Copy coordinates to clipboard utility function
 * Can be used in both React components and raw HTML contexts
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @returns {Promise<boolean>} - Whether copy was successful
 */
export const copyCoordinatesToClipboard = async (latitude, longitude) => {
  return copyTextToClipboard(`${latitude}, ${longitude}`);
};

/**
 * Copy Coordinates Button Component
 * Displays a button that copies lat/long coordinates to clipboard
 * @param {{ latitude: number, longitude: number, className?: string, size?: string, variant?: string }} props
 * @returns {JSX.Element|null} Copy coordinates button or null if no coordinates
 */
export default function CopyCoordinatesButton({
  latitude,
  longitude,
  className = "",
  size = "sm",
  variant = "outline",
}) {
  const [isCopied, setIsCopied] = useState(false);

  if (latitude == null || longitude == null) {
    return null;
  }

  const handleCopyCoordinates = async () => {
    const success = await copyCoordinatesToClipboard(latitude, longitude);
    if (success) {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      className={cn(steelChromeButton, className)}
      onClick={handleCopyCoordinates}>
      {isCopied ? (
        <Check className='h-4 w-4 mr-2' />
      ) : (
        <Copy className='h-4 w-4 mr-2' />
      )}
      {isCopied ? "Copied!" : "Copy Coordinates"}
    </Button>
  );
}
